/* @sravni/react-header version 0.2.43 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useBoolean, useAuthLogoutLink, preventBodyScroll } from '@sravni/react-utils';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Avatar } from '@sravni/react-design-system/lib/Avatar';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Divider } from '@sravni/react-design-system/lib/Divider';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { RegionDialog } from '@sravni/react-region-dialog';
import { Cross, User, Location, Logout } from '@sravni/react-icons';
import '../../DropdownMenu/index.js';
import '../../InjectedIcon/index.js';
import '../../DownloadApp/index.js';
import { useAnalyticEvents, EVENTS } from '../../../hooks/useAnalyticEvents.js';
import { defaultUserNavigation } from '../../../mocks/defaultUserNavigation.js';
import modules_8de2575d from '../styles/MobileHeader.module.less.js';
import InjectedIcon from '../../InjectedIcon/src/InjectedIcon.js';
import { DownloadApp } from '../../DownloadApp/src/DownloadApp.js';
import DropdownMenu from '../../DropdownMenu/src/DropdownMenu.js';

/**
 * @description Mobile user block component.
 *
 * @component
 * @example
 * ```jsx
 * <MobileUserBlock />
 * ```
 */
var MobileUserBlock = React.forwardRef(function (props, ref) {
    var serviceURL = props.serviceURL, _a = props.settings, settings = _a === void 0 ? {} : _a, user = props.user, regions = props.regions, currentRegion = props.currentRegion, visible = props.visible, className = props.className, oidcHandlerPrefix = props.oidcHandlerPrefix, _b = props.domains, domains = _b === void 0 ? {} : _b, formatPath = props.formatPath, onOpen = props.onOpen, onClose = props.onClose, restProps = __rest(props, ["serviceURL", "settings", "user", "regions", "currentRegion", "visible", "className", "oidcHandlerPrefix", "domains", "formatPath", "onOpen", "onClose"]);
    var cx = useStyles(modules_8de2575d);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _c = __read(useBoolean(false), 2), visibleRegionDialog = _c[0], setVisibleRegionDialog = _c[1];
    var authLogoutLinkProps = useAuthLogoutLink({ prefix: oidcHandlerPrefix, isNewWindow: false });
    var userNavigation = React.useMemo(function () { return settings.userMenu || defaultUserNavigation; }, [settings]);
    var handleLinkClick = React.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent(EVENTS.MENU_LINK_CLICK, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|" + label });
    }, []);
    var handleLogout = React.useCallback(function () {
        pushAnalyticEvent(EVENTS.LOGOUT);
    }, []);
    var handleRegionDialogOpen = React.useCallback(function () {
        setVisibleRegionDialog.on();
        pushAnalyticEvent(EVENTS.REGION_DIALOG_CLICK, { eventLabel: "\u0412\u044B\u0437\u043E\u0432 \u043F\u043E\u043F\u0430\u043F\u0430" });
    }, []);
    var handleRegionDialogChange = React.useCallback(function (region) {
        pushAnalyticEvent(EVENTS.REGION_DIALOG_CLICK, { eventLabel: region.name });
    }, []);
    React.useEffect(function () {
        if (visible) {
            preventBodyScroll(true);
        }
        else {
            preventBodyScroll();
        }
    }, [visible]);
    var userAvatar = React.useMemo(function () {
        if (visible) {
            return (React.createElement(Button, { className: cx('customButton'), size: 36, variant: "primary", color: "gray", onClick: onClose },
                React.createElement(Icon, { icon: React.createElement(Cross, null) })));
        }
        if (user && user.sub) {
            return React.createElement(Avatar, { size: 36, src: "//f.sravni.ru/avatars/" + user.sub + ".svg", onClick: onOpen });
        }
        return (React.createElement(Icon, { size: 16, color: "gray", className: modules_8de2575d.userIcon, onClick: onOpen },
            React.createElement(User, null)));
    }, [user, visible]);
    var mobileUserMenu = React.useMemo(function () {
        var topGroup = (userNavigation || []).slice(0, -1);
        var bottomGroup = (userNavigation || []).slice(-1);
        return (React.createElement("div", { className: cx('userMenu') },
            topGroup.map(function (item) { return (React.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('userMenuItem') },
                React.createElement(Space, { align: "center", size: 12 },
                    !!item.iconSrc && (React.createElement(Icon, { size: 20, icon: React.createElement(InjectedIcon, { iconSrc: item.iconSrc }) })),
                    item.icon && !item.iconSrc ? (React.createElement(Icon, { size: 20, icon: React.createElement("img", { src: item.icon }) })) : null,
                    React.createElement(Text, { size: 16, strong: true }, item.text)))); }),
            React.createElement(Divider, { className: cx('userMenuDivider') }),
            React.createElement(DownloadApp, { isMobile: true }),
            React.createElement(Link, { color: "black", onClick: handleRegionDialogOpen, className: cx('userMenuItem') },
                React.createElement(Space, { align: "center", size: 12 },
                    React.createElement(Icon, { icon: React.createElement(Location, null) }),
                    React.createElement(Space, { direction: "vertical" },
                        React.createElement(Text, { size: 12, className: "h-color-D30" }, "\u041C\u043E\u0439 \u0433\u043E\u0440\u043E\u0434"),
                        React.createElement(Text, { size: 16, strong: true }, (currentRegion === null || currentRegion === void 0 ? void 0 : currentRegion.name) ? currentRegion.name : 'Выберите город')))),
            bottomGroup.map(function (item) { return (React.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('userMenuItem') },
                React.createElement(Space, { align: "center", size: 12 },
                    !!item.iconSrc && (React.createElement(Icon, { size: 20, icon: React.createElement(InjectedIcon, { iconSrc: item.iconSrc }) })),
                    item.icon && !item.iconSrc ? (React.createElement(Icon, { size: 20, icon: React.createElement("img", { src: item.icon }) })) : null,
                    React.createElement(Text, { size: 16, strong: true }, item.text)))); }),
            React.createElement(Divider, { className: cx('userMenuDivider') }),
            React.createElement(Link, __assign({ color: "gray" }, authLogoutLinkProps, { className: cx('userMenuItem'), onClick: handleLogout }),
                React.createElement(Space, { align: "center", size: 12 },
                    React.createElement(Icon, { icon: React.createElement(Logout, null) }),
                    React.createElement(Text, { size: 16, strong: true, className: "h-color-D60" }, "\u0412\u044B\u0439\u0442\u0438")))));
    }, [authLogoutLinkProps, userNavigation, currentRegion]);
    return (React.createElement(Space, __assign({ ref: ref, "data-qa": "MobileUserBlock", align: "center", className: cx(className, 'top-nav') }, restProps),
        React.createElement("div", { className: cx('top-nav__item', 'top-nav__item--clean') },
            userAvatar,
            React.createElement(DropdownMenu, { wide: true, fullscreen: true, style: { display: visible ? 'block' : 'none' } }, mobileUserMenu)),
        React.createElement(RegionDialog, { fullscreen: true, serviceURL: serviceURL, formatPath: formatPath, regions: regions, visible: visibleRegionDialog, onClose: setVisibleRegionDialog.off, onChange: handleRegionDialogChange })));
});
MobileUserBlock.defaultProps = {};

export { MobileUserBlock as default };
//# sourceMappingURL=MobileUserBlock.js.map
